import { useTranslate } from 'module/hooks/use-translation';
import { Grid as BaseGrid } from 'module/ui/components/atoms/grid/grid';
import { FC, useMemo } from 'react';
import styled from 'styled-components';
import { ActionCell } from './renderers/action-cell';
import { FilterCell } from './renderers/filter-cell';
import { NameCell } from './renderers/name-cell';
import { SectionData } from './types';
import type { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';

const Grid = styled(BaseGrid)`
  .ag-cell-focus {
    cursor: pointer;
    border: none !important;
    background-color: transparent !important;
  }
  .ag-group-expanded,
  .ag-group-contracted {
    display: none !important;
  }
  .ag-row-group-leaf-indent {
    margin-left: var(--spacing-04) !important;
  }
  .ag-row-group-leaf-indent.ag-row-group-indent-0 {
    padding-left: 0 !important;
    margin-left: 0 !important;
  }
`;

const flattenTree = (nodes: SectionData[], parentPath: string[] = []): any[] =>
  nodes.flatMap((node) => {
    const currentPath = [...parentPath, node.sectionId];
    const flatNode = { ...node, path: currentPath };
    const children = node.children
      ? flattenTree(node.children, currentPath)
      : [];

    return [flatNode, ...children];
  });

interface DynamicSectionTableProps {
  onEditFilter: (section: SectionData) => void;
  onDeleteFilter: (section: SectionData) => void;
  gridApi?: GridApi;
  onGridReady: (event: GridReadyEvent) => void;
  sectionData: SectionData[];
}

export const DynamicSectionTable: FC<DynamicSectionTableProps> = ({
  onEditFilter,
  onDeleteFilter,
  onGridReady,
  sectionData,
}) => {
  const translate = useTranslate();
  const columnDefs = useMemo<ColDef<any>[]>(
    () => [
      {
        headerName: translate(
          'people_page_templates.dynamic_section.table.header.dynamic',
        ),
        field: 'dynamic',
        flex: 1,
        cellRendererParams: {
          onEditFilter,
        },
        cellRenderer: FilterCell,
      },
      {
        headerName: '',
        width: 88,
        cellRenderer: ActionCell,
        cellRendererParams: {
          onEditFilter,
          onDeleteFilter,
        },
        sortable: false,
        filter: false,
      },
    ],
    [translate, onEditFilter, onDeleteFilter],
  );
  const rowData = useMemo(() => {
    return flattenTree(sectionData);
  }, [sectionData]);

  return (
    <Grid
      onGridReady={onGridReady}
      rowData={rowData}
      columnDefs={columnDefs}
      treeData
      getDataPath={(data: any) => data.path}
      groupDefaultExpanded={-1}
      domLayout="autoHeight"
      suppressRowClickSelection
      defaultColDef={{
        filter: false,
        sortable: false,
        resizable: false,
        wrapText: true,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
      }}
      suppressContextMenu
      autoGroupColumnDef={{
        headerName: translate(
          'people_page_templates.dynamic_section.table.header.blocks',
        ),
        flex: 1,
        cellRendererParams: {
          suppressCount: true,
          innerRenderer: NameCell,
          suppressIcons: true,
        },
      }}
    />
  );
};
